import React from "react";
import { Box, Container, Typography, Paper, Chip, Stack, LinearProgress } from "@mui/material";
import { Link as RouterLink, useParams } from "react-router-dom";
import MarketingNav from "../components/MarketingNav";
import SeoHead from "../components/SeoHead";
import { BRAND_ASSETS, BRAND_COLORS, brandFullLogoImgSx } from "../utils/brandAssets";
import { useGetTriviaShareResultQuery } from "../store/api/mlbApi";
import { getLetterGrade } from "../utils/triviaArcade";
import { buildShareCardHeadline } from "../utils/triviaShareCard";
import { SPORT_BY_ID } from "../constants/sportWorkspace";
import { SITE_ORIGIN } from "../utils/seoCatalog";

function formatCompletedAt(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

const TriviaShareVerify = () => {
  const { token } = useParams();
  const { data: result, isLoading, error } = useGetTriviaShareResultQuery(token, {
    skip: !token,
  });

  const sportLabel = SPORT_BY_ID[result?.sport]?.label || "MLB";
  const headline = buildShareCardHeadline(sportLabel, result?.gameMode);
  const grade = getLetterGrade(result?.accuracy || 0);
  const completedAt = formatCompletedAt(result?.completedAt);
  const verified = Boolean(result) && !error;

  return (
    <>
      <SeoHead
        title={
          verified
            ? `${result.username} — ${headline} | Stats.Fan`
            : "Trivia result | Stats.Fan"
        }
        description="Verify a stats.fan trivia score. This page confirms a completed game without showing questions or answers."
        canonical={token ? `${SITE_ORIGIN}/v/${token}` : undefined}
        robots="noindex, nofollow"
      />
      <MarketingNav />
      <Box sx={{ pt: { xs: 10, md: 12 }, pb: 8, minHeight: "100vh" }}>
        <Container maxWidth="sm">
          <Box
            component="img"
            src={BRAND_ASSETS.fullLogo}
            alt="stats.fan"
            sx={{ ...brandFullLogoImgSx, maxWidth: 220, mx: "auto", mb: 3 }}
          />
          {isLoading ? <LinearProgress /> : null}
          {!isLoading && !verified ? (
            <Paper sx={{ p: 3, textAlign: "center" }}>
              <Typography variant="h5" fontWeight={800} sx={{ mb: 1 }}>
                Not a verified result
              </Typography>
              <Typography color="text.secondary">
                This link is not a completed stats.fan trivia score.
              </Typography>
            </Paper>
          ) : null}
          {verified ? (
            <Paper sx={{ p: { xs: 3, sm: 4 }, textAlign: "center" }}>
              <Chip
                label="Verified result"
                sx={{
                  mb: 2,
                  bgcolor: BRAND_COLORS.cyan,
                  color: BRAND_COLORS.navy,
                  fontWeight: 700,
                }}
              />
              <Typography variant="overline" display="block">
                {headline}
              </Typography>
              <Typography
                variant="h1"
                fontWeight={900}
                sx={{ color: "#ec4899", lineHeight: 1, my: 1 }}
              >
                {grade}
              </Typography>
              <Typography variant="h4" fontWeight={800}>
                {result.score}
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 1 }}>
                {result.correctAnswers}/{result.totalQuestions} correct ·{" "}
                {Math.round((result.accuracy || 0) * 100)}% accuracy
              </Typography>
              <Stack spacing={0.5} sx={{ mt: 3 }}>
                <Typography fontWeight={700}>@{result.username}</Typography>
                {completedAt ? (
                  <Typography variant="body2" color="text.secondary">
                    Completed {completedAt}
                  </Typography>
                ) : null}
              </Stack>
            </Paper>
          ) : null}
          <Typography
            component={RouterLink}
            to="/app/trivia"
            sx={{
              display: "block",
              mt: 3,
              textAlign: "center",
              color: BRAND_COLORS.cyan,
              textDecoration: "none",
            }}
          >
            Play trivia on stats.fan
          </Typography>
        </Container>
      </Box>
    </>
  );
};

export default TriviaShareVerify;
